/**
 * `GET /tenants/{t}/credits`, and the state derived from it plus the funded
 * window.
 *
 * Invalidated by nothing here. The reflex is to invalidate this query after
 * a credit request is submitted, and that would be wrong: a credit request
 * (§18.6) is only ever a request -- it moves to `OPEN`, not to a grant, and
 * grants nothing itself. There is no event after which the balance a tenant
 * already has could have changed, so there is nothing to invalidate it with.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { ApiFailureError } from '../../../lib/http/errors';
import { useApiClient } from '../../session/client';
import { type Credits } from '../api/schemas';
import { acquisitionState, type AcquisitionState } from './state';
import { useFundedWindow } from './useFundedWindow';
import { GET_ROUTES, routePath } from '../../../lib/http/routes';

export const CREDITS_QUERY_KEY = (tenantId: string) => ['tenant', tenantId, 'credits'] as const;

export function useCredits(tenantId: string): UseQueryResult<Credits, ApiFailureError> {
  const client = useApiClient();

  return useQuery<Credits, ApiFailureError>({
    queryKey: CREDITS_QUERY_KEY(tenantId),
    queryFn: () =>
      client.getParsed(routePath(GET_ROUTES.credits, { tenantId }), GET_ROUTES.credits.schema),
  });
}

export function useAcquisitionState(tenantId: string): AcquisitionState {
  const credits = useCredits(tenantId);
  const window = useFundedWindow(tenantId);
  return acquisitionState(credits.data, window.data);
}
